import { Button } from "react-bulma-components";
import { useTranslation } from "react-i18next";
import { useBoard, removeBoardMembership } from "../../../models/board";
import { useUserName } from "../../../models/user";
import { useInvites, resolveInvite } from "../../../models/invite";
import { InviteData } from "../../../models/invite.types";
import { ModalProps } from "../ModalManager";

export type BoardMembersModalProps = {
  boardUid: string;
  ownerUid: string;
} & ModalProps;

function MemberRow({ memberUid, boardUid, isOwner }: { memberUid: string; boardUid: string; isOwner: boolean }) {
  const { t } = useTranslation();
  const [name, loading] = useUserName(memberUid);

  const handleRemove = () => {
    removeBoardMembership(boardUid, memberUid);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.25rem 0" }}>
      <span>{loading ? "\u00A0" : name ?? t("modal.members.unknown-user")}</span>
      {isOwner
        ? <span className="tag is-info">{t("modal.members.owner-badge")}</span>
        : <Button size="small" color="warning" onClick={handleRemove}>
            <span className="icon is-small"><i className="icon-user-delete" aria-hidden="true"></i></span>
            <span>{t("modal.members.remove")}</span>
          </Button>
      }
    </div>
  );
}

function InviteRow({ invite, boardUid }: { invite: InviteData; boardUid: string }) {
  const { t } = useTranslation();

  const handleAccept = () => {
    resolveInvite(boardUid, invite.uid, "approve");
  };

  const handleDeny = () => {
    resolveInvite(boardUid, invite.uid, "deny");
  };

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.25rem 0" }}>
      <div>
        <span>{invite.name}</span>
        <br />
        <span className="has-text-grey is-size-7">{invite.email}</span>
      </div>
      <div className="buttons are-small">
        <Button size="small" color="success" onClick={handleAccept}>
          <span className="icon is-small"><i className="icon-user-add" aria-hidden="true"></i></span>
          <span>{t("modal.members.accept")}</span>
        </Button>
        <Button size="small" color="danger" onClick={handleDeny}>
          <span className="icon is-small"><i className="icon-user-delete" aria-hidden="true"></i></span>
          <span>{t("modal.members.deny")}</span>
        </Button>
      </div>
    </div>
  );
}

export function BoardMembersModal({ boardUid, ownerUid }: BoardMembersModalProps) {
  const { t } = useTranslation();
  const [board] = useBoard(boardUid);
  const [invites] = useInvites(boardUid);

  const memberUids = board ? Array.from(board.memberUids.keys()) : [];
  const pendingInvites = invites ?? [];

  return (
    <div className="box">
      <p className="title is-4">{t("modal.members.title")}</p>

      <p className="subtitle is-5">{t("modal.members.section-members")}</p>
      {memberUids.map((uid) => (
        <MemberRow key={uid} memberUid={uid} boardUid={boardUid} isOwner={uid === ownerUid} />
      ))}

      <hr />
      {pendingInvites.length > 0 ? (
        <>
          <p className="subtitle is-5">{t("modal.members.section-requests")}</p>
          {pendingInvites.map((invite) => (
            <InviteRow key={invite.uid} invite={invite} boardUid={boardUid} />
          ))}
        </>
      ) : (
        <p className="has-text-grey">{t("modal.members.no-requests")}</p>
      )}
    </div>
  );
}
